import React from "react";

/*
 * ============================================================
 * SHIMMER KEYFRAMES
 * ============================================================
 * Injected once via a <style> tag since this is a plain
 * inline-styled component (no CSS module / styled-components
 * in the original file).
 */

const ShimmerStyles = () => (
  <style>
    {`
      @keyframes bom-skeleton-shimmer {
        0% { background-position: -400px 0; }
        100% { background-position: 400px 0; }
      }
    `}
  </style>
);

/*
 * ============================================================
 * BASE SKELETON BLOCK
 * ============================================================
 * A single shimmering rectangle. Compose these to match the
 * shape of the real content.
 */

const Bone = ({
  width = "100%",
  height = "12px",
  radius = "6px",
  style = {},
}) => (
  <div
    style={{
      width,
      height,
      borderRadius: radius,
      flexShrink: 0,
      background:
        "linear-gradient(90deg, #eef0f4 25%, #f7f8fa 37%, #eef0f4 63%)",
      backgroundSize: "800px 100%",
      animation: "bom-skeleton-shimmer 1.4s ease-in-out infinite",
      ...style,
    }}
  />
);

/*
 * ============================================================
 * INFO CARD SKELETON
 * ============================================================
 * Matches the small bordered stat cards in the product
 * information grid (Unit 1 / Unit 2 / Conversion / Tax /
 * Decimal / Status).
 */

const InfoCardSkeleton = ({ minWidth }) => (
  <div
    style={{
      flex: `1 1 ${minWidth}`,
      minWidth,
      minHeight: "60px",
      padding: "8px 11px",
      boxSizing: "border-box",
      borderRadius: "7px",
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: "8px",
    }}
  >
    <Bone width="46%" height="8px" radius="4px" />
    <Bone width="70%" height="13px" radius="4px" />
  </div>
);

/*
 * ============================================================
 * MAPPED TEST ROW SKELETON
 * ============================================================
 * Matches one row in the "Selected Tests" list on the left.
 */

const MappedTestRowSkeleton = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",

      minHeight: "42px",

      padding: "0 8px",

      marginBottom: "5px",

      border: "1px solid #e2e8f0",

      borderRadius: "7px",

      background: "#ffffff",
      boxSizing: "border-box",
    }}
  >
    <Bone width="24px" height="24px" radius="6px" />

    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
      <Bone width="60%" height="10px" radius="4px" />
      <Bone width="35%" height="8px" radius="4px" />
    </div>

    <Bone width="25px" height="25px" radius="5px" />
  </div>
);

/*
 * ============================================================
 * PRODUCT DETAILS / TEST BOM SKELETON
 * ============================================================
 */

const ProductDetailsSkeleton = ({ cardStyle, rows = 4 }) => {
  return (
    <div
      style={{
        ...cardStyle,
        marginTop: "12px",
        overflow: "hidden",
      }}
    >
      <ShimmerStyles />

      {/* =========================================================
          PRODUCT HEADER
      ========================================================== */}
      <div
        style={{
          minHeight: "70px",
          padding: "0 18px",
          borderBottom: "1px solid #ede9fe",
          background: "#faf9ff",

          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",

          gap: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            minWidth: 0,
            flex: 1,
          }}
        >
          <Bone width="42px" height="42px" radius="9px" />

          <div style={{ minWidth: 0, flex: 1, maxWidth: "260px", display: "flex", flexDirection: "column", gap: "8px" }}>
            <Bone width="65%" height="14px" radius="4px" />
            <Bone width="85%" height="9px" radius="4px" />
          </div>
        </div>

        <Bone width="34px" height="34px" radius="7px" />
      </div>

      {/* =========================================================
          PRODUCT INFORMATION
      ========================================================== */}

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          padding: "12px 18px",
          borderBottom: "1px solid #f1f5f9",
          background: "#ffffff",
        }}
      >
        <InfoCardSkeleton minWidth="140px" />
        <InfoCardSkeleton minWidth="140px" />
        <InfoCardSkeleton minWidth="170px" />
        <InfoCardSkeleton minWidth="100px" />
        <InfoCardSkeleton minWidth="100px" />
        <InfoCardSkeleton minWidth="130px" />
      </div>

      {/* =========================================================
          BOM HEADER
      ========================================================== */}
      <div
        style={{
          padding: "12px 18px 9px",
          display: "flex",
          alignItems: "center",
          gap: "9px",
        }}
      >
        <Bone width="21px" height="21px" radius="5px" />

        <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
          <Bone width="90px" height="12px" radius="4px" />
          <Bone width="200px" height="8px" radius="4px" />
        </div>
      </div>

      {/* =========================================================
          BOM CONTENT
      ========================================================== */}
      <div
        style={{
          padding: "0 18px 18px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(240px, 0.9fr) minmax(310px, 1.1fr)",
            gap: "12px",
            minHeight: "290px",
          }}
        >
          {/* =====================================================
              LEFT SIDE
          ====================================================== */}

          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              background: "#ffffff",
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            {/* SEARCH */}

            <div
              style={{
                padding: "8px",
                borderBottom: "1px solid #f1f5f9",
                background: "#fafafa",
              }}
            >
              <Bone width="100%" height="38px" radius="6px" />
            </div>

            {/* MAPPED TESTS */}

            <div
              style={{
                padding: "8px",
              }}
            >
              <div style={{ padding: "2px 2px 10px" }}>
                <Bone width="120px" height="9px" radius="4px" />
              </div>

              <div>
                {Array.from({ length: rows }).map((_, index) => (
                  <MappedTestRowSkeleton key={index} />
                ))}
              </div>
            </div>
          </div>

          {/* =====================================================
              RIGHT SIDE - CONFIGURATION
          ====================================================== */}

          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              background: "#ffffff",
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            {/* CONFIG HEADER */}

            <div
              style={{
                minHeight: "58px",

                padding: "0 12px",

                display: "flex",
                alignItems: "center",

                borderBottom: "1px solid #ede9fe",

                background: "#faf9ff",
              }}
            >
              <Bone width="34px" height="34px" radius="7px" />

              <div
                style={{
                  marginLeft: "8px",
                  minWidth: 0,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <Bone width="55%" height="11px" radius="4px" />
                <Bone width="35%" height="8px" radius="4px" />
              </div>
            </div>

            {/* CONFIG CONTENT */}

            <div
              style={{
                padding: "12px",
              }}
            >
              {/* BOM TYPE LABEL */}

              <Bone
                width="110px"
                height="10px"
                radius="4px"
                style={{ marginBottom: "9px" }}
              />

              {/* BOM TYPE OPTIONS */}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "7px",
                }}
              >
                {[0, 1].map((index) => (
                  <div
                    key={index}
                    style={{
                      minHeight: "56px",

                      display: "flex",
                      alignItems: "center",

                      gap: "9px",

                      padding: "7px 9px",

                      border: "1px solid #e2e8f0",

                      borderRadius: "7px",

                      background: "#ffffff",

                      boxSizing: "border-box",
                    }}
                  >
                    <Bone width="16px" height="16px" radius="50%" />

                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <Bone width="70%" height="10px" radius="4px" />
                      <Bone width="85%" height="8px" radius="4px" />
                    </div>
                  </div>
                ))}
              </div>

              {/* QUANTITY / CONFIG BLOCK */}

              <div
                style={{
                  marginTop: "10px",

                  padding: "9px 10px",

                  border: "1px solid #e2e8f0",

                  borderRadius: "7px",

                  background: "#f8fafc",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    <Bone width="55%" height="10px" radius="4px" />
                    <Bone width="80%" height="8px" radius="4px" />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 }}>
                    <Bone width="64px" height="34px" radius="5px" />
                    <Bone width="90px" height="34px" radius="5px" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          FOOTER
      ========================================================== */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "0 18px 18px",
        }}
      >
        <Bone width="120px" height="10px" radius="4px" />
        <Bone width="110px" height="36px" radius="6px" />
      </div>
    </div>
  );
};

export default ProductDetailsSkeleton;
